import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_serramenti-design/artifacts/08hkgxg6_finestre%20e%20colore%20logo%20%281%29.png";

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#missione", label: "Missione" },
  { href: "#servizi", label: "Servizi" },
  { href: "#galleria", label: "Galleria" },
  { to: "/progetti", label: "Progetti" },
  { href: "#contatti", label: "Contatti" },
];

export default function Navigation({ mobileMenuOpen, setMobileMenuOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (!isHome) {
      navigate("/" + href);
      setMobileMenuOpen(false);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const renderDesktopLink = (link) => {
    if (link.to) {
      return (
        <Link
          key={link.to}
          to={link.to}
          onClick={() => setMobileMenuOpen(false)}
          className="nav-link text-[#0B1E36] font-medium hover:text-[#007AFF] transition-colors"
          data-testid={`nav-link-${link.label.toLowerCase()}`}
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.href}
        href={link.href}
        onClick={(e) => scrollToSection(e, link.href)}
        className="nav-link text-[#0B1E36] font-medium hover:text-[#007AFF] transition-colors"
        data-testid={`nav-link-${link.label.toLowerCase()}`}
      >
        {link.label}
      </a>
    );
  };

  const renderMobileLink = (link) => {
    if (link.to) {
      return (
        <Link
          key={link.to}
          to={link.to}
          onClick={() => setMobileMenuOpen(false)}
          className="text-[#0B1E36] font-medium px-4 py-2 hover:bg-[#F1F5F9] rounded-lg transition-colors"
          data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.href}
        href={link.href}
        onClick={(e) => scrollToSection(e, link.href)}
        className="text-[#0B1E36] font-medium px-4 py-2 hover:bg-[#F1F5F9] rounded-lg transition-colors"
        data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
      >
        {link.label}
      </a>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => scrollToSection(e, "#home")}
            className="flex items-center"
            data-testid="nav-logo"
          >
            <img
              src={LOGO_URL}
              alt="Finestre e Colore"
              className="h-14 md:h-16 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(renderDesktopLink)}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              onClick={(e) => scrollToSection(e, "#preventivo")}
              className="btn-shine bg-[#0B1E36] hover:bg-[#0B1E36]/90 text-white font-semibold px-6 py-2.5 rounded-full"
              data-testid="nav-cta-button"
            >
              Richiedi Preventivo
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#0B1E36]"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mobile-menu-enter bg-white border-t border-[#E2E8F0] py-4">
            <div className="flex flex-col gap-4">
              {navLinks.map(renderMobileLink)}
              <div className="px-4 pt-2">
                <Button
                  onClick={(e) => scrollToSection(e, "#preventivo")}
                  className="w-full bg-[#0B1E36] hover:bg-[#0B1E36]/90 text-white font-semibold py-3 rounded-full"
                  data-testid="mobile-nav-cta-button"
                >
                  Richiedi Preventivo
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
